// src/pages/ViewReport/GenerationReportView.js

import React, { useMemo } from 'react';
import BeforeAfterSlider from '../../components/Onboarding/BeforeAfterSlider';
import BenefitsDisplay from '../../components/Onboarding/BenefitsDisplay';
import WhatsAppLogo from '../../images/WhatsAppLogo.png';
import matchMedicLogo from '../../images/matchmedic_logo_white.png';



/**
 * Progress Bar Component
 */
function ProgressBar({ 
  percent, 
  label, 
  minLabel = 'Low', 
  maxLabel = 'High', 
  color = 'bg-white', 
  trackColor = 'bg-white/30',
  labelTextColor = '#111827',
}) {
  const widthStyle = { width: `${Math.max(0, Math.min(100, percent)).toFixed(1)}%` };
  
  return (
    <div className="w-full">
      <div className={`relative w-full h-6 rounded-full ${trackColor}`}> 
        <div 
          className={`absolute inset-y-0 left-0 rounded-full ${color} flex items-center overflow-hidden`} 
          style={widthStyle}
        >
          {label && (
            <span 
              className="ml-2 inline-flex items-center rounded-full bg-white/90 px-2 py-0.5 text-xs font-semibold shadow-sm whitespace-nowrap" 
              style={{ color: labelTextColor }}
            >
              {label}
            </span>
          )}
        </div>
      </div>
      <div className="mt-2 flex w-full justify-between text-xs text-white/90">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
    </div>
  );
}

/**
 * Gradient Card colors (matching BeforeAfterSlide)
 */
const CARD_GRADIENTS = {
  candidateScore: {
    from: '#41537C',
    to: '#6387DB',
  },
  expectedSatisfaction: {
    from: '#AC3FBC',
    to: '#E570F7',
  },
};

/**
 * Colored Card Component
 */
function ColoredCard({ title, subtitle, gradientColors, children }) {
  return (
    <div 
      className="rounded-2xl p-4 sm:p-5 shadow-lg text-white"
      style={{
        background: `linear-gradient(to bottom right, ${gradientColors.from}, ${gradientColors.to})`
      }}
    > 
      <div className="mb-3">
        <h3 className="text-base sm:text-lg font-semibold leading-snug">{title}</h3>
        {subtitle && (
          <p className="mt-1 text-xs sm:text-sm text-white/90">{subtitle}</p>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * GenerationReportView - Displays generated hair preview results.
 * 
 * Simplified version of BeforeAfterSlide's FINAL state:
 * - Before/After slider comparison
 * - Score cards (Candidate Score, Expected Satisfaction)
 * - WhatsApp contact card
 * - Benefits display
 * - CTA for next steps
 * 
 * Props:
 * @param {string} originalImageUrl - URL of the original/before image
 * @param {string} generatedImageUrl - URL of the generated/after image
 * @param {object} metadata - Report metadata
 * @param {number} candidateScorePercent - Candidate score percentage
 * @param {number} expectedSatisfactionPercent - Expected satisfaction percentage
 * @param {function} onSwitchTab - Callback to switch to analysis tab
 * @param {boolean} hasAnalysis - Whether analysis data is available
 */
export default function GenerationReportView({
  originalImageUrl,
  generatedImageUrl,
  metadata,
  candidateScorePercent = 72,
  expectedSatisfactionPercent = 92,
  onSwitchTab,
  hasAnalysis = false,
  activeTab = 'generation',
  onTabChange = () => {},
  // View report tracking props
  viewReportId = null,
  reportRequestId = null,
  onContactButtonClick = () => {},
  onPhoneSubmission = () => {},
  onEmailSubmission = () => {},
}) {
  // Extract user info
  const displayFirstName = useMemo(() => {
    const name = metadata?.userName || '';
    if (!name) return '';
    const firstName = name.trim().split(/\s+/)[0] || '';
    if (!firstName) return '';
    return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
  }, [metadata]);

  const hasComparisonImages = !!(originalImageUrl && generatedImageUrl);

  // Color constants (matching AnalysisReportView)
  const colors = {
    topBanner: '#1f1f1f',
    subBanner: '#3a3a3a',
    subBannerText: '#9ca3af',
  };

  return (
    <div className="min-h-screen flex flex-col relative" style={{ backgroundColor: '#2a2a2a' }}>
      {/* Top Banner - Full width black background */}
      <div className="w-full" style={{ backgroundColor: colors.topBanner }}>
        <div className="w-full lg:w-2/5 mx-auto flex items-center justify-center px-4 py-3 sm:py-4">
          {/* Logo - Centered */}
          <img 
            src={matchMedicLogo} 
            alt="Match Medic" 
            className="h-6"
            style={{ maxWidth: '140px', objectFit: 'contain' }}
          />
        </div>
      </div>

      {/* Main Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-4 pt-4" style={{ paddingBottom: '200px' }}>
        <div className="mx-auto w-full max-w-md">
          <div className="space-y-6">
            {/* Before/After Section */}
            {hasComparisonImages ? (
              <div>
                {/* Slider */}
                <BeforeAfterSlider
                  beforeImage={originalImageUrl}
                  afterImage={generatedImageUrl}
                  beforeLabel="Before"
                  afterLabel="After"
                  initialPosition={50}
                  className="shadow-lg"
                />
                <p className="mt-3 text-xs text-gray-400 text-center leading-relaxed">
                  This is not a medical diagnosis. Match Medic uses advanced algorithms. Results may vary.
                </p>
              </div>
            ) : (
              <div className="mb-4">
                {!generatedImageUrl && (
                  <div className="aspect-square bg-gray-800 rounded-2xl flex items-center justify-center">
                    <span className="text-gray-400">Preview not available</span>
                  </div>
                )}
              </div>
            )}

            {/* Analysis Cards Section */}
            <div className="space-y-4 mt-8">
              {/* Hair Transplant Candidate Score Card */}
              <ColoredCard
                title="Hair Transplant Candidate Score"
                subtitle="How suitable are you for a hair transplant"
                gradientColors={CARD_GRADIENTS.candidateScore}
              >
                <ProgressBar 
                  percent={candidateScorePercent} 
                  label={`${candidateScorePercent}%`}
                  minLabel="Low" 
                  maxLabel="High"
                  labelTextColor={CARD_GRADIENTS.candidateScore.from}
                />
              </ColoredCard>
              
              {/* Expected Satisfaction Card */}
              <ColoredCard
                title="Expected Satisfaction"
                subtitle="Based on 1000+ reviews matched to your attributes"
                gradientColors={CARD_GRADIENTS.expectedSatisfaction}
              >
                <ProgressBar 
                  percent={expectedSatisfactionPercent} 
                  label={`${expectedSatisfactionPercent}%`}
                  minLabel="Low" 
                  maxLabel="High"
                  labelTextColor={CARD_GRADIENTS.expectedSatisfaction.from}
                />
              </ColoredCard>
              
              {/* Benefits Display */}
              <BenefitsDisplay />
            </div>
          </div>
        </div>
      </div>

      {/* Floating Bottom Island - WhatsApp Contact Card */}
      <div className="fixed bottom-0 left-0 right-0 z-50">
        <div className="w-full lg:w-2/5 mx-auto px-4 pb-4">
          <div 
            className="rounded-2xl p-4 sm:p-5 shadow-2xl"
            style={{ 
              background: 'linear-gradient(to bottom right, #BCBCBC, #EAEAEA)',
              boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)',
            }}
          >
            {/* Top Section - WhatsApp */}
            <div className="flex items-center justify-between gap-1">
              <p className="flex-1 text-base font-semibold text-gray-800 leading-snug">
                Connect with our consultants right now
              </p>
              
              <a
                href="https://wa.me/908503469160?text=Hi!%20I'm%20interested%20in%20your%20services."
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-gray-900 px-4 py-2.5 transition hover:bg-gray-800"
                style={{ minWidth: '120px' }}
                onClick={() => onContactButtonClick('whatsapp', 'generation_contact_card')}
              >
                <img 
                  src={WhatsAppLogo} 
                  alt="WhatsApp" 
                  className="h-5 w-5 object-contain" 
                />
                <span className="text-sm font-medium text-white whitespace-nowrap">
                  New Chat
                </span>
              </a>
            </div>
            
            {/* Separator with OR */}
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-gray-400/50" />
              <span className="text-sm font-medium text-gray-500">OR</span>
              <div className="flex-1 h-px bg-gray-400/50" />
            </div>
            
            {/* Bottom Section - Let us reach you */}
            <div className="flex items-center justify-between gap-1">
              <p className="flex-1 text-base font-semibold text-gray-800 leading-snug">
                Let us reach you
              </p>
              
              <a
                href={`/contact-info${viewReportId && reportRequestId ? `?vr=${encodeURIComponent(viewReportId)}&rid=${encodeURIComponent(reportRequestId)}` : ''}`}
                className="flex-shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-gray-900 px-4 py-2.5 transition hover:bg-gray-800"
                style={{ minWidth: '120px' }}
                onClick={() => onContactButtonClick('contact_info_link', 'generation_contact_card')}
              >
                <span className="text-sm font-medium text-white whitespace-nowrap">
                  Contact Info
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
